import { useState, useMemo } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import {
  Visibility,
  Edit,
  Delete,
  Add,
  StorageRounded,
  WarningAmber,
  ListRounded,
  CodeRounded
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Avatar,
  Stack,
  CircularProgress
} from "@mui/material";
import ResourceServersUpdate from "./resource-servers-update";
import ResourceServersDetail from "./resource-servers-detail";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import moment from "moment";
import Error from "../error";
import useUserPermissions from "../../hooks/useUserPermissions";
import {
  mrtManagementTableCustomActionsOuterSx,
  mrtManagementTablePrimaryActionButtonSx,
  mrtManagementTableToolbarOptions,
} from "../../utils/mrtManagementTableToolbar";

// Resource Servers Management component powered by Material React Table
function ResourceServers() {
  const { createResourceServer, getAllResourceServers } = useUserPermissions();

  // MRT State Management
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: "created_at", desc: false }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });

  // Dialog & View State
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(false);
  const [update, setUpdate] = useState(false);
  const [selectedServerId, setSelectedServerId] = useState(null);
  const [selectedServerData, setSelectedServerData] = useState({});

  // Safety Confirmation States
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, server: null });
  const [editConfirm, setEditConfirm] = useState({ open: false, server: null });

  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  // Data Fetching Logic (Server-Side)
  const {
    isLoading: serversDataLoading,
    data: serversData,
    refetch,
    error: fetchError,
  } = useQuery(
    ["resource-servers", pagination, globalFilter, columnFilters, sorting],
    () => {
      const filterParams = globalFilter
        ? JSON.stringify([{
          column_field: "*",
          operator_value: "contains",
          value: JSON.stringify({
            columns: ["name"],
            value: globalFilter
          })
        }])
        : columnFilters.length > 0
          ? JSON.stringify(columnFilters.map(f => ({
            column_field: f.id,
            operator_value: "contains",
            value: f.value
          })))
          : "[]";

      const sortParams = JSON.stringify(sorting.map(s => ({
        field: s.id,
        sort: s.desc ? "desc" : "asc"
      })));

      return axiosPrivate.get(`/resourceServers`, {
        params: {
          page: pagination.pageIndex,
          per_page: pagination.pageSize,
          sort: sortParams,
          filter: filterParams,
        },
      });
    },
    { refetchOnWindowFocus: false, enabled: !!getAllResourceServers }
  );

  // Mutations
  const { mutate: deleteServerMutate, isLoading: isDeleting } = useMutation((id) =>
    axiosPrivate.delete(`/resourceServers/${id}`),
    {
      onSuccess: () => {
        enqueueSnackbar("Resource Server deleted successfully", { variant: "success" });
        setDeleteConfirm({ open: false, server: null });
        refetch();
      }
    }
  );

  // Handlers
  const handleView = (id) => {
    setSelectedServerId(id);
    setView(true);
    setOpen(true);
  };

  const handleEditRequest = (server) => {
    setSelectedServerData(server);
    setEditConfirm({ open: true, server });
  };

  const handleProceedEdit = () => {
    setSelectedServerId(selectedServerData.id);
    setUpdate(true);
    setView(false);
    setOpen(true);
    setEditConfirm({ open: false, server: null });
  };

  const handleOpenCreate = () => {
    setUpdate(false);
    setView(false);
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
    setUpdate(false);
    setView(false);
  };

  // Columns Definition
  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Service Architecture",
      size: 250,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondary.lighter', color: 'secondary.main', borderRadius: 2 }}>
            <StorageRounded fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main' }}>
              {row.original.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '11px' }}>
              ID: {row.original.id}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      accessorKey: "scopes",
      header: "Defined Scopes",
      size: 200,
      Cell: ({ cell }) => (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {cell.getValue()?.slice(0, 3).map((s, i) => (
            <Chip key={i} label={s.name} size="small" variant="outlined" sx={{ fontSize: '10px', height: '20px', fontWeight: 600 }} />
          ))}
          {cell.getValue()?.length > 3 && (
            <Chip label={`+${cell.getValue().length - 3}`} size="small" variant="outlined" sx={{ fontSize: '10px', height: '20px', fontWeight: 600 }} />
          )}
        </Stack>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Registered",
      size: 150,
      Cell: ({ cell }) => moment(cell.getValue()).format("MMM D, YYYY"),
    },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: serversData?.data?.data || [],
    initialState: { showColumnFilters: false, density: 'comfortable' },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    ...mrtManagementTableToolbarOptions,
    muiToolbarAlertBannerProps: fetchError ? { color: 'error', children: 'Error loading data' } : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: serversData?.data?.meta_data?.total || 0,
    state: { columnFilters, globalFilter, isLoading: serversDataLoading, pagination, sorting },
    // enableRowActions: true,
    positionActionsColumn: 'last',
    // renderRowActions: ({ row }) => (
    //   <Box sx={{ display: 'flex', gap: 0.5 }}>
    //     <Tooltip title="Details">
    //       <IconButton size="small" onClick={() => handleView(row.original.id)} sx={{ opacity: 0.7 }}>
    //         <Visibility fontSize="small" />
    //       </IconButton>
    //     </Tooltip>
    //     <Tooltip title="Edit">
    //       <IconButton size="small" onClick={() => handleEditRequest(row.original)} color="primary">
    //         <Edit fontSize="small" />
    //       </IconButton>
    //     </Tooltip>
    //     <Tooltip title="Delete">
    //       <IconButton size="small" onClick={() => setDeleteConfirm({ open: true, server: row.original })} color="error">
    //         <Delete fontSize="small" />
    //       </IconButton>
    //     </Tooltip>
    //   </Box>
    // ),
    renderTopToolbarCustomActions: () => (
      <Box sx={mrtManagementTableCustomActionsOuterSx}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'secondary.lighter', display: 'flex', flexShrink: 0 }}>
            <StorageRounded sx={{ color: 'secondary.main' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'secondary.main', lineHeight: 1.2 }}>
              Resource Servers
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {serversData?.data?.meta_data?.total || 0} APIs securing your resources
            </Typography>
          </Box>
        </Stack>
        {createResourceServer && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={mrtManagementTablePrimaryActionButtonSx}
          >
            Add Resource Server
          </Button>
        )}
      </Box>
    ),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 5,
        border: "1px solid", borderColor: "divider",
        boxShadow: '0 10px 40px rgba(0,0,0,0.02)',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        bgcolor: 'background.default',
        fontWeight: 800,
        color: 'text.secondary',
        fontSize: '12px',
        py: 2,
      },
    },
  });

  return fetchError ? (
    <Error error={fetchError?.response?.data?.error} />
  ) : (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100%' }}>
      <MaterialReactTable table={table} />

      {/* Modals for Detail / Update */}
      <Modal open={open} onClose={handleModalClose}>
        <Box>
          {view ? (
            <ResourceServersDetail handleViewModalClose={handleModalClose} serverId={selectedServerId} />
          ) : (
            <ResourceServersUpdate handleModalClose={handleModalClose} isNew={!update} serverData={selectedServerData} refetch={refetch} />
          )}
        </Box>
      </Modal>

      {/* Delete Confirmation */}
      <SafetyDialog
        open={deleteConfirm.open}
        title="Delete Resource Server?"
        content={`The API "${deleteConfirm.server?.name}" and all its scopes will be permanently removed. Proceed?`}
        onConfirm={() => deleteServerMutate(deleteConfirm.server?.id)}
        onCancel={() => setDeleteConfirm({ open: false, server: null })}
        isLoading={isDeleting}
        color="error"
      />

      {/* Edit Confirmation */}
      <SafetyDialog
        open={editConfirm.open}
        title="Edit Configuration?"
        content={`Changing configurations for "${editConfirm.server?.name}" may impact applications relying on these scopes. Proceed?`}
        onConfirm={handleProceedEdit}
        onCancel={() => setEditConfirm({ open: false, server: null })}
        color="warning"
      />
    </Box>
  );
}

function SafetyDialog({ open, title, content, onConfirm, onCancel, isLoading, color = "primary" }) {
  return (
    <Dialog
      open={open}
      onClose={() => !isLoading && onCancel()}
      PaperProps={{ sx: { borderRadius: 6, p: 1, maxWidth: '400px' } }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Box sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: `${color}.lighter`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2
        }}>
          <WarningAmber color={color} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>{content}</Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
        <Button fullWidth variant="outlined" color="inherit" onClick={onCancel} disabled={isLoading} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button fullWidth variant="contained" color={color} onClick={onConfirm} disabled={isLoading} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ResourceServers;
